import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useRequiredContext } from "@redotech/react-util/context";
import { isAeoAllowed } from "@redotech/redo-aeo-common/is-aeo-allowed";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import CatalogIcon from "@redotech/redo-icons/catalog.svg";
import CatalogIconFilled from "@redotech/redo-icons/catalog_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import BrandIcon from "@redotech/redo-icons/diamond-01.svg";
import MessageIcon from "@redotech/redo-icons/message-text-square-02.svg";
import IntegrationsIcon from "@redotech/redo-icons/share-07.svg";
import IntegrationsFilledIcon from "@redotech/redo-icons/share-07_filled.svg";
import TargetIcon from "@redotech/redo-icons/target-01.svg";
import CompetitorsIcon from "@redotech/redo-icons/users-01.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useLocation } from "react-router-dom";
import { AeoCatalogOptimizationItem } from "./aeo-catalog-optimization";
import { AeoCatalogProductsItem } from "./aeo-catalog-products";

export function useAeoNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);

  if (!isAeoAllowed(team?.settings.aeo)) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "aeo",
    hasNotification: false,
    UnfilledIcon: TargetIcon,
    FilledIcon: TargetIcon,
    type: "link",
    href: routes.aeoDashboardHref,
    pathRoot: routes.aeoBaseUrl,
    friendlyName: "AEO",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "aeo",
    name: "AEO",
    NavContent: SecondaryAeoNavigation,
  };

  return { primaryTab, secondaryNavigationData, isActiveProduct: true };
}

const SecondaryAeoNavigation = memo(function SecondaryAeoNavigation() {
  const user = useContext(UserContext);

  const isRedoEmployee = isRedoEmailAddress(user?.email);

  const { id: nodeId, TreeContext } = useNavTreeNode({ friendlyName: "AEO" });

  return (
    <TreeContext.Provider value={nodeId}>
      <AeoDashboardItem />
      <AeoAssistantItem />
      <AeoBrandItem />
      <AeoCatalogItem />
      <AeoCompetitorsItem />
      {isRedoEmployee && <AeoIntegrationsItem />}
      <AeoPromptsItem />
    </TreeContext.Provider>
  );
});

const AeoDashboardItem = memo(function AeoDashboardItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.aeoDashboardHref }}
      FilledIcon={DashboardFilledIcon}
      friendlyName="Dashboard"
      id="aeo-dashboard"
      UnfilledIcon={DashboardIcon}
    />
  );
});

const AeoAssistantItem = memo(function AeoAssistantItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.aeoAssistantHref }}
      beta
      FilledIcon={AiStarIcon}
      friendlyName="Assistant"
      id="aeo-assistant"
      UnfilledIcon={AiStarIcon}
    />
  );
});

const AeoBrandItem = memo(function AeoBrandItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.aeoBrandHref }}
      FilledIcon={BrandIcon}
      friendlyName="Brand"
      id="aeo-brand"
      UnfilledIcon={BrandIcon}
    />
  );
});

const AeoCatalogItem = memo(function AeoCatalogItem() {
  const team = useRequiredContext(TeamContext);
  const location = useLocation();

  const isCatalogEnabled = team.settings.ims?.catalogEnabled ?? false;

  const isCatalogChildSelected =
    location.pathname.includes("/aeo/catalog/") ||
    location.pathname.endsWith("/aeo/catalog");

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isCatalogChildSelected}
      FilledIcon={CatalogIconFilled}
      friendlyName="Catalog"
      hidden={!isCatalogEnabled}
      id="aeo-catalog"
      isFolder
      UnfilledIcon={CatalogIcon}
    >
      <AeoCatalogOptimizationItem />
      <AeoCatalogProductsItem />
    </RedoSecondaryNavItem>
  );
});

const AeoCompetitorsItem = memo(function AeoCompetitorsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.aeoCompetitorsHref }}
      FilledIcon={CompetitorsIcon}
      friendlyName="Competitors"
      id="aeo-competitors"
      UnfilledIcon={CompetitorsIcon}
    />
  );
});

const AeoIntegrationsItem = memo(function AeoIntegrationsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.aeoIntegrationsHref }}
      FilledIcon={IntegrationsFilledIcon}
      friendlyName="Integrations"
      id="aeo-integrations"
      UnfilledIcon={IntegrationsIcon}
    />
  );
});

const AeoPromptsItem = memo(function AeoPromptsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.aeoPromptsHref }}
      FilledIcon={MessageIcon}
      friendlyName="Prompts"
      id="aeo-prompts"
      UnfilledIcon={MessageIcon}
    />
  );
});
